import {
  getErrorCode,
  GraphQLRequestError,
} from "@myvp/shared/src/functions/get-error-code"
import { HttpCode } from "@myvp/shared/src/types"

const isMockConst = isMock()

export function selectService<T>(args: { real: T; mock: NoInfer<T> }): T {
  return isMockConst ? args.mock : args.real
}

export function isMock(): boolean {
  const searchParams = new URLSearchParams(window.location.search)
  const mock = searchParams.get("mock")
  const json = searchParams.get("json")

  return (
    (!!mock || !!json) &&
    (process.env.NODE_ENV === "development" ||
      !!process.env.REACT_APP_mock_services)
  )
}

/**
 * NOTE: Type-safe assert version is `assertGraphQLRequestError`.
 */
export function handleGraphQLErrors(
  data:
    | {
        [key: string]: GraphQLRequestError | object
      }
    | undefined
) {
  if (data === undefined) {
    return {}
  }

  // @ts-expect-error
  if (Object.keys(data).length === 1 && data.response?.errors) {
    throw {
      response: data.response,
    }
  }
  return data ?? {}
}

export async function getMockData() {
  const searchParams = new URLSearchParams(window.location.search)
  const json = searchParams.get("json")
  if (!json) {
    return {}
  }
  if (json.endsWith(".ts")) {
    if (json.includes("src") || json.includes("/mocks")) {
      // oxlint-disable-next-line no-alert
      window.alert(
        "When using a .ts file as your mock, do not include mocks or src. Specify the file from `src/mocks/`"
      )
    }
    try {
      const result = await import(`src/mocks/${json}`)
      return result.default
    } catch (e) {
      // oxlint-disable-next-line no-console
      console.error(e)
      throw e
    }
  }
  const result = await fetch(json)
  return result.json()
}

export function consumeMockRestResponse(
  _response: {
    status?: string | number
    _httpHeaders?: Headers | null
    Status?: string
  } = {
    status: HttpCode.Ok,
    _httpHeaders: null,
  }
) {
  const response = { ..._response }
  const errorCode = getErrorCode(response)
  if (
    errorCode === HttpCode.Ok ||
    errorCode === HttpCode.NoContent ||
    !errorCode
  ) {
    response._httpHeaders = new Headers()
    return response
  } else {
    throw response
  }
}

/**
 * Asserts the data passed is not a GraphQLRequestError. This is a type-safe assert version of `handleGraphQLErrors`
 *
 * This uses assert types, so the data passed in will be narrowed to not include the `GraphQLRequestError` type.
 * Refrain from passing an explicit type parameter, because that can act similarly to a type assertion (`as`).
 *
 * @param data throw if this is a GraphQLRequestError
 *
 * @see GraphQLRequestError
 */
export function assertGraphQLRequestError<T>(
  data: T | GraphQLRequestError
): asserts data is T {
  // NOTE: assert types are similar to type guards, so this impl needs to be a bit more strict.
  // lots of mock data flows through this function, so try not to match actual data.
  if (
    typeof data === "object" &&
    data !== null &&
    Object.keys(data).length === 1 &&
    "response" in data &&
    typeof data.response === "object" &&
    "errors" in data.response
  ) {
    throw { response: data.response }
  }
}
